import api from './api';
import { sanitizeFile } from '../utils/fileUtils';
import { mediaService } from './mediaService';

export const userService = {
  getProfile: async (username) => {
    try {
      const response = await api.get(`/users/username/${username}`);
      // Backend provides complete URLs, no additional processing needed
      return response.data;
    } catch (error) {
      console.error('UserService: API call failed:', error);
      console.error('UserService: Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers
      });
      
      if (error.response) {
        if (error.response.status === 401) {
          throw new Error('Please log in to view this profile');
        }
        throw new Error(error.response.data.message || 'Failed to fetch profile');
      } else if (error.request) {
        throw new Error('No response from server. Please check your connection.');
      } else {
        throw new Error('Failed to send request. Please try again.');
      }
    }
  },

  getCompleteProfile: async (username) => {
    try {
      const response = await api.get(`/users/profile/${username}`);
      // Backend provides complete URLs and accurate stats, no additional processing needed
      return response.data;
    } catch (error) {
      console.error('UserService: Optimized API call failed:', error);
      console.error('UserService: Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers
      });
      
      if (error.response) {
        if (error.response.status === 401) {
          throw new Error('Please log in to view this profile');
        }
        throw new Error(error.response.data.message || 'Failed to fetch profile');
      } else if (error.request) {
        throw new Error('No response from server. Please check your connection.');
      } else {
        throw new Error('Failed to send request. Please try again.');
      }
    }
  },
  
  updateProfile: async (userId, userData) => {
    try {
      const response = await api.put(`/users/${userId}`, userData);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        throw new Error('Please log in to update your profile');
      }
      throw new Error(error.response?.data?.message || 'Failed to update profile');
    }
  },
  
  getSubscriptions: async () => {
    try {
      const response = await api.get('/users/subscriptions');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        throw new Error('Please log in to view subscriptions');
      }
      throw new Error(error.response?.data?.message || 'Failed to fetch subscriptions');
    }
  },
  
  getSubscribers: async () => {
    try {
      const response = await api.get('/users/subscribers');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        throw new Error('Please log in to view subscribers');
      }
      throw new Error(error.response?.data?.message || 'Failed to fetch subscribers');
    }
  },

  subscribeToUser: async (userId) => {
    try {
      const response = await api.post(`/users/${userId}/subscribe`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        throw new Error('Please log in to subscribe');
      }
      throw new Error(error.response?.data?.message || 'Failed to subscribe');
    }
  },

  unsubscribeFromUser: async (userId) => {
    try {
      const response = await api.post(`/users/${userId}/unsubscribe`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        throw new Error('Please log in to unsubscribe');
      }
      throw new Error(error.response?.data?.message || 'Failed to unsubscribe');
    }
  },

  /**
   * Subscribe to a creator's plan with payment
   * Uses an existing stored payment method
   * 
   * @param {string} creatorId - The creator's user ID
   * @param {string} planId - The subscription plan ID
   * @param {string} storedPaymentMethodId - The stored payment method to charge
   * @returns {Promise<Object>} Subscription details
   */
  subscribeToUserPlan: async (creatorId, planId, storedPaymentMethodId) => {
    try {
      const response = await api.post(`/users/${creatorId}/subscribe/${planId}/pay`, {
        storedPaymentMethodId
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        throw new Error('Please log in to subscribe');
      }
      if (error.response?.status === 400) {
        throw new Error(error.response?.data?.message || 'Invalid subscription request');
      }
      if (error.response?.status === 402) {
        throw new Error(error.response?.data?.message || 'Payment failed');
      }
      throw new Error(error.response?.data?.message || 'Failed to subscribe');
    }
  },

  checkSubscriptionStatus: async (creatorId) => {
    try {
      const response = await api.get(`/users/${creatorId}/subscription-status`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        throw new Error('Please log in to check subscription status');
      }
      throw new Error(error.response?.data?.message || 'Failed to check subscription status');
    }
  },

  searchUsers: async (query, connectedOnly = false) => {
    try {
      const params = new URLSearchParams({ query });
      if (connectedOnly) {
        params.append('connectedOnly', 'true');
      }
      
      const response = await api.get(`/users/search?${params}`);
      return response.data;
    } catch (error) {
      console.error('userService: Search error:', error);
      if (error.response?.status === 401) {
        throw new Error('Please log in to search users');
      }
      throw new Error(error.response?.data?.message || 'Failed to search users');
    }
  },

  searchUsersForTagging: async (query, page = 0, size = 15) => {
    try {
      // Require at least 3 characters
      if (!query || query.trim().length < 3) {
        return { content: [], totalPages: 0, totalElements: 0, number: 0 };
      }
      
      const response = await api.get(`/users/search/tags?query=${encodeURIComponent(query.trim())}&page=${page}&size=${size}`);
      return response.data;
    } catch (error) {
      console.error('userService: Tag search error:', error);
      return { content: [], totalPages: 0, totalElements: 0, number: 0 };
    }
  },

  getAllUsers: async () => {
    try {
      const response = await api.get('/users');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        throw new Error('Please log in to view users');
      }
      throw error.response?.data || { message: 'Failed to fetch users' };
    }
  },

  uploadProfilePicture: async (userId, file, onProgress) => {
    try {
      // Sanitize the filename before upload
      const sanitizedFile = sanitizeFile(file);
      
      // Step 1: Upload directly to S3 via presigned URL
      const uploadResult = await mediaService.uploadFileDirectToS3(
        sanitizedFile,
        'profile', // context for presigned URL
        onProgress
      );
      
      // Step 2: Tell backend to process the uploaded file
      const response = await api.post(`/users/${userId}/profile-picture/s3`, {
        s3Key: uploadResult.s3Key
      });
      
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        throw new Error('Please log in to update your profile picture');
      }
      throw new Error(error.response?.data?.message || error.message || 'Failed to upload profile picture');
    }
  },

  uploadBannerImage: async (userId, file, onProgress) => {
    try {
      // Sanitize the filename before upload
      const sanitizedFile = sanitizeFile(file);
      
      // Step 1: Upload directly to S3 via presigned URL
      const uploadResult = await mediaService.uploadFileDirectToS3(
        sanitizedFile,
        'banner', // context for presigned URL
        onProgress
      );
      
      // Step 2: Tell backend to process the uploaded file
      const response = await api.post(`/users/${userId}/banner-image/s3`, {
        s3Key: uploadResult.s3Key
      });
      
      return response.data;
    } catch (error) {
      console.error('userService: Banner upload error:', error);
      if (error.response?.status === 401) {
        throw new Error('Please log in to update your banner image');
      }
      throw new Error(error.response?.data?.message || error.message || 'Failed to upload banner image');
    }
  },

  removeProfileImage: async (userId) => {
    try {
      const response = await api.delete(`/users/${userId}/profile-picture`);
      return response.data;
    } catch (error) {
      console.error('userService: Remove profile picture error:', error);
      if (error.response?.status === 401) {
        throw new Error('Please log in to remove your profile picture');
      }
      throw new Error(error.response?.data?.message || 'Failed to remove profile picture');
    }
  },

  removeBannerImage: async (userId) => {
    try {
      const response = await api.delete(`/users/${userId}/banner-image`);
      return response.data;
    } catch (error) {
      console.error('userService: Remove banner image error:', error);
      if (error.response?.status === 401) {
        throw new Error('Please log in to remove your banner image');
      }
      throw new Error(error.response?.data?.message || 'Failed to remove banner image');
    }
  },

  getCreators: async () => {
    try {
      const response = await api.get('/users/creators');
      return response.data;
    } catch (error) {
      console.error('userService: Get creators error:', error);
      if (error.response?.status === 401) {
        throw new Error('Please log in to view creators');
      }
      throw new Error(error.response?.data?.message || 'Failed to fetch creators');
    }
  },

  /**
   * Search creators who allow being tagged (for system tagging modal) with pagination
   * Requires minimum 3 characters to search
   * @param {string} query - Search term (min 3 chars)
   * @param {number} page - Page number (0-indexed)
   * @param {number} size - Page size
   */
  searchCreatorsForTagging: async (query = '', page = 0, size = 20) => {
    try {
      // Require at least 3 characters
      if (!query || query.trim().length < 3) {
        return { content: [], totalPages: 0, totalElements: 0, number: 0 };
      }
      const response = await api.get(`/users/creators?forTagging=true&query=${encodeURIComponent(query.trim())}&page=${page}&size=${size}`);
      return response.data;
    } catch (error) {
      console.error('userService: Search creators for tagging error:', error);
      return { content: [], totalPages: 0, totalElements: 0, number: 0 };
    }
  },

  /**
   * Get the "Keep it Going" (auto-renew) status for a subscription
   * @param {string} userId - Current user's ID
   * @param {string} subscriptionId - The subscription ID
   * @returns {Promise<Object>} Rebill status details
   */
  getKeepItGoingStatus: async (userId, subscriptionId) => {
    try {
      const response = await api.get(`/users/${userId}/subscriptions/${subscriptionId}/rebill`);
      return response.data;
    } catch (error) {
      console.error('userService: Get Keep it Going status error:', error);
      if (error.response?.status === 401) {
        throw new Error('Please log in to view subscription status');
      }
      throw new Error(error.response?.data?.message || 'Failed to fetch subscription status');
    }
  },

  /**
   * Toggle "Keep it Going" (auto-renew) for a subscription
   * @param {string} userId - Current user's ID
   * @param {string} subscriptionId - The subscription ID
   * @param {boolean} enabled - Whether to enable or disable auto-renew
   * @param {string} reason - Optional reason for the change
   * @returns {Promise<Object>} Updated rebill status
   */
  toggleKeepItGoing: async (userId, subscriptionId, enabled, reason = '') => {
    try {
      const response = await api.put(`/users/${userId}/subscriptions/${subscriptionId}/rebill`, {
        rebillEnabled: enabled,
        reason: reason || (enabled ? 'User enabled Keep it Going' : 'User disabled Keep it Going')
      });
      return response.data;
    } catch (error) {
      console.error('userService: Toggle Keep it Going error:', error);
      if (error.response?.status === 401) {
        throw new Error('Please log in to update subscription');
      }
      if (error.response?.status === 400) {
        throw new Error(error.response?.data?.message || 'Cannot update subscription settings');
      }
      throw new Error(error.response?.data?.message || 'Failed to update subscription');
    }
  },

  // ==================== TRUSTED DEVICES ====================

  /**
   * Get all trusted devices for the current user
   * Used in Security Settings to show the list of recognized devices
   * @returns {Promise<Array>} List of trusted devices
   */
  getTrustedDevices: async () => {
    try {
      const response = await api.get('/users/me/devices');
      return response.data;
    } catch (error) {
      console.error('userService: Get trusted devices error:', error);
      if (error.response?.status === 401) {
        throw new Error('Please log in to view trusted devices');
      }
      throw new Error(error.response?.data?.message || 'Failed to fetch trusted devices');
    }
  },

  /**
   * Remove a trusted device by ID
   * @param {string} deviceId - The device ID to remove
   * @returns {Promise<Object>} Success message
   */
  removeTrustedDevice: async (deviceId) => {
    try {
      const response = await api.delete(`/users/me/devices/${deviceId}`);
      return response.data;
    } catch (error) {
      console.error('userService: Remove trusted device error:', error);
      if (error.response?.status === 401) {
        throw new Error('Please log in to remove devices');
      }
      throw new Error(error.response?.data?.message || 'Failed to remove device');
    }
  },

  /**
   * Remove all trusted devices except the current one
   * Effectively logs out from all other devices
   * @returns {Promise<Object>} Result with removed count
   */
  removeAllOtherDevices: async () => {
    try {
      const response = await api.delete('/users/me/devices');
      return response.data;
    } catch (error) {
      console.error('userService: Remove all devices error:', error);
      if (error.response?.status === 401) {
        throw new Error('Please log in to manage devices');
      }
      throw new Error(error.response?.data?.message || 'Failed to remove devices');
    }
  },

  getUsersBatch: async (ids, view = 'default') => {
    if (!ids || ids.length === 0) return {};
    const response = await api.get('/users/batch', { params: { ids: ids.join(','), view } });
    return response.data; // { [uuid]: UserSummaryDTO }
  },
};